import { monotonicFactory } from 'ulid';
import { db } from '../db/db.js';
import type { EventPayloadMap, EventType, InventoryEvent } from './types.js';
import { applyEventToProjections } from './projector.js';

const DEFAULT_DEVICE_ID = 'local';
const DEFAULT_ACTOR_ID = 'self';

// 單調 ULID factory：保證同一 ms 內也維持遞增，
// 讓 events.orderBy('id') 等同 causal 次序（rebuildProjections / sync replay 必要）
const eventUlid = monotonicFactory();

export interface DispatchContext {
  deviceId: string;
  actorId: string;
}

let context: DispatchContext = {
  deviceId: DEFAULT_DEVICE_ID,
  actorId: DEFAULT_ACTOR_ID,
};

export function setDispatchContext(next: Partial<DispatchContext>): void {
  context = { ...context, ...next };
}

/**
 * Append an event and apply its projection in a single transaction.
 *
 * Domain logic should never write projected tables directly; dispatch is the
 * only path that mutates state, keeping the event log as source of truth (ADR-013).
 */
export async function dispatch<T extends EventType>(
  type: T,
  payload: EventPayloadMap[T]
): Promise<InventoryEvent<EventPayloadMap[T]>> {
  const event: InventoryEvent<EventPayloadMap[T]> = {
    id: eventUlid(),
    deviceId: context.deviceId,
    actorId: context.actorId,
    timestamp: Date.now(),
    type,
    payload,
  };
  await db.transaction(
    'rw',
    [db.events, db.items, db.shoppingItems, db.subscriptions, db.settings],
    async () => {
      await db.events.add(event as InventoryEvent);
      await applyEventToProjections(event as InventoryEvent);
    },
  );
  return event;
}

export async function listEvents(): Promise<InventoryEvent[]> {
  return db.events.orderBy('id').toArray();
}

export async function eventCount(): Promise<number> {
  return db.events.count();
}
